import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const ApplePage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Apple Success: Perfect pH for Premium Fruit"
        description="Research-backed pH management for profitable apple production. Learn how water quality affects your apple orchard yield and quality."
        canonicalPath="/apple"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Apples.jpg"
            alt="Apple orchard with red apples on trees"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/80 via-red-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🍎</span>
              <span>Premium Orchard Resource</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Apple Success:
              <span className="block text-red-300">Perfect pH for Premium Fruit</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management for profitable apple production
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-red-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-red-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-red-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🍎</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-red-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Apples are sensitive to soil pH and water quality</strong> — and your irrigation water could be quietly undermining your orchard's productivity and fruit quality season after season.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped apple growers optimize their water quality for 40+ years. This guide explains why your irrigation water pH matters for apples, what research shows, and how sulfur burners can protect your soil, enhance your fruit quality, and maximize your profits.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Ideal Soil pH for Apples */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Ideal Soil pH for Apples
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 md:p-8 border border-red-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-red-500 pl-6">
                "Apples grow best in moderately acidic soils, typically between pH 6.0 and 7.0. Soils with higher pH can tie up micronutrients like iron and zinc, leading to leaf yellowing and poor fruit quality."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-red-600" size={16} />
                <span className="text-sm font-medium text-red-600">
                  Source: Washington State University Tree Fruit — Soil and Nutrition Management
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Even if your soil started at a healthy pH, irrigating with high-bicarbonate water can gradually raise it over time. This can tie up nutrients just when your trees need them for fruit fill and bud set.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Hidden Problem - Bicarbonates in Irrigation Water */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💧</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: Hidden Problem — Bicarbonates in Irrigation Water
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Think of bicarbonates as tiny pH "elevators" in your water. When you irrigate with high-bicarbonate water, 
                these compounds slowly but steadily raise soil pH. It's like adding lime to your 
                apple orchard, drop by drop, season after season. Even if your soil pH starts in the optimal range, 
                alkaline irrigation water can gradually push it above the sweet spot for apple production.
              </p>

              <blockquote className="text-lg text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6 bg-white rounded-lg p-4">
                "High bicarbonate levels in irrigation water can gradually increase soil pH, causing infiltration issues and nutrient imbalances in orchard systems."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <span className="text-sm font-medium text-orange-600">
                  Source: Michigan State University Extension — Irrigation Water Quality for Fruit Production
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">If your irrigation water tests above ~120 ppm bicarbonates, it's time to think about inline acidification to protect your soil pH and reduce expensive soil amendments later.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Impact on Drip & Microirrigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🚜</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Impact on Drip & Microirrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-blue-500 pl-6">
                "Carbonate and bicarbonate salts in irrigation water can precipitate as lime scale inside drip emitters, partially or completely plugging them."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-blue-600" size={16} />
                <span className="text-sm font-medium text-blue-600">
                  Source: UC ANR — Microirrigation of Trees and Vines
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Plugged emitters cause uneven water distribution — which can mean uneven fruit sizing and more stress on your trees. Inline acidification helps dissolve these salts before they build up.</p>
              </div>

              {/* Problem vs Solution Visual */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ High-Bicarbonate Water Problems:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Clogged drip emitters</li>
                    <li>• Uneven water distribution</li>
                    <li>• Uneven fruit sizing</li>
                    <li>• Increased tree stress</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With pH Management:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean, efficient emitters</li>
                    <li>• Uniform water application</li>
                    <li>• Consistent fruit sizing</li>
                    <li>• Reduced tree stress</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: PNW Insight - Irrigation Water Quality */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🌲</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: PNW Insight — Irrigation Water Quality for Apples
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Tree fruit growers in the Pacific Northwest should regularly test irrigation water for pH and bicarbonates to maintain healthy soil conditions for tree longevity and yield."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-green-600" size={16} />
                <span className="text-sm font-medium text-green-600">
                  Source: WSU Tree Fruit — Irrigation Water Quality
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Don't assume your water is safe — bicarbonates creep up year after year, raising soil pH and tying up nutrients.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Scale Buildup with Overhead Watering */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🌧️</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Scale Buildup with Overhead Watering
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-purple-500 pl-6">
                "Mineral scale from hard or alkaline water can coat fruit, branches, and foliage, especially under overhead sprinklers. This reduces light penetration, increases disease pressure, and impacts fruit quality."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-purple-600" size={16} />
                <span className="text-sm font-medium text-purple-600">
                  Source: UC ANR — Agricultural Salinity and Drainage (FAO Soils Bulletin)
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Inline acidification reduces carbonate and bicarbonate levels — which means less scale formation on fruit and leaves, cleaner canopies, and better fruit finish for your packouts.</p>
              </div>

              {/* Visual Example */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ Scale Buildup Problems:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Mineral coating on fruit</li>
                    <li>• Reduced light penetration</li>
                    <li>• Increased disease pressure</li>
                    <li>• Poor fruit finish quality</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With Acidification:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean fruit surfaces</li>
                    <li>• Better light penetration</li>
                    <li>• Reduced disease risk</li>
                    <li>• Premium packout quality</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 6: Bonus Bicarbonate Threshold */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                6: Bonus Bicarbonate Threshold
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 md:p-8 border border-teal-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-teal-500 pl-6">
                "When irrigation water contains over 120 ppm bicarbonates, soils become prone to alkalinity and infiltration decline. Neutralizing bicarbonates can help maintain ideal pH ranges for tree fruit."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-teal-600" size={16} />
                <span className="text-sm font-medium text-teal-600">
                  Source: UCANR — Salinity and Agriculture Resource
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Keeping bicarbonates under control protects your soil pH sweet spot and orchard ROI.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Apples:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents emitter clogging in drip systems',
                    'Reduces scale buildup on fruit and foliage',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    '40+ years of proven results in apple orchards'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Apples" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ApplePage;